/**
 * <revamp-cart-upsell>
 * In-cart upsell carousel for the revamped cart drawer (Figma "Cart - New").
 *
 * - Lazily fetches `data-url` (the product-recommendations endpoint rendering the
 *   cart-drawer section with recommendations.performed == true), extracts the
 *   rendered <revamp-cart-upsell> markup, and injects the cards.
 * - Turns the cards into a single-card, scroll-snap carousel with pagination dots.
 * - Add-to-cart is handled natively by the theme's [is="product-form"]; after a
 *   cart change the drawer re-renders and a fresh instance re-fetches.
 * - Self-contained: when there are no recommendations it hides itself only; it
 *   never removes the surrounding cart-drawer section.
 *
 * Guarded so the AJAX section re-render (which re-inserts this script tag but does
 * not re-execute it) never throws "already defined".
 */
if (!customElements.get('revamp-cart-upsell')) {
  class RevampCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      // The server always renders a fallback (manual list / collection / all), so
      // show it immediately — the upsell is never empty on mobile or desktop.
      if (this.querySelector('.revamp-upsell__slide')) {
        this.hidden = false;
        this.buildCarousel();
      }

      // Then try to UPGRADE to real complementary recommendations in the background.
      if (this.dataset.url) {
        const start = this.load.bind(this);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(start, { timeout: 2000 });
        } else {
          setTimeout(start, 800);
        }
      }
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this._onScroll);
    }

    async load() {
      if (this._loaded) return;
      this._loaded = true;

      // Try complementary, then related. Only REPLACE the visible fallback when
      // the response is genuine recommendations with cards.
      const urls = [this.dataset.url];
      if (this.dataset.url.includes('intent=complementary')) {
        urls.push(this.dataset.url.replace('intent=complementary', 'intent=related'));
      }

      for (const url of urls) {
        try {
          const text = await (await fetch(url)).text();
          const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('revamp-cart-upsell');
          const isRecs = fresh && fresh.getAttribute('data-source') === 'recommendations';
          if (isRecs && fresh.querySelector('.revamp-upsell__slide')) {
            this.innerHTML = fresh.innerHTML.trim();
            this.hidden = false;
            this.buildCarousel();
            return;
          }
        } catch (error) {
          // try the next source / keep the fallback
        }
      }

      // No recommendations to upgrade to — keep the server fallback. Hide only if
      // there is genuinely nothing to show (empty store).
      if (!this.querySelector('.revamp-upsell__slide')) this.hidden = true;
    }

    buildCarousel() {
      this.viewport = this.querySelector('[data-revamp-upsell-viewport]');
      this.dotsContainer = this.querySelector('[data-revamp-upsell-dots]');
      this.slides = Array.from(this.querySelectorAll('.revamp-upsell__slide'));

      if (!this.viewport || this.slides.length <= 1) {
        if (this.dotsContainer) this.dotsContainer.hidden = true;
        return;
      }

      // Build dots.
      this.dotsContainer.hidden = false;
      this.dotsContainer.innerHTML = '';
      this.dots = this.slides.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'revamp-upsell__dot';
        dot.setAttribute('aria-label', `Go to recommendation ${index + 1} of ${this.slides.length}`);
        dot.addEventListener('click', () => this.goTo(index));
        this.dotsContainer.appendChild(dot);
        return dot;
      });

      this._onScroll = this.rafThrottle(this.syncActiveDot.bind(this));
      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
      this.syncActiveDot();
    }

    goTo(index) {
      const slide = this.slides[index];
      if (slide) this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
    }

    syncActiveDot() {
      const width = this.viewport.clientWidth || 1;
      const index = Math.round(this.viewport.scrollLeft / width);
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    rafThrottle(fn) {
      let ticking = false;
      return () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          fn();
          ticking = false;
        });
      };
    }
  }

  customElements.define('revamp-cart-upsell', RevampCartUpsell);
}

/**
 * Auto-open the cart drawer when the cart grows (parity with the live theme).
 * Bound once at the document level (AJAX section re-renders re-insert this script
 * but don't re-run it). Opens on the theme's `ajaxProduct:added` event and on any
 * cartUpdate where the item count increases — so it fires for adds from anywhere
 * (PDP form, quick-add, app widgets) but NOT for in-drawer qty/remove (the drawer
 * is already open, and show() is a no-op when open).
 */
if (!window.__revampCartAutoOpenBound) {
  window.__revampCartAutoOpenBound = true;

  const openDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function' && !drawer.open) drawer.show();
  };

  document.addEventListener('ajaxProduct:added', openDrawer);

  if (typeof theme !== 'undefined' && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
    const readCount = () => {
      const el = document.querySelector('cart-count');
      const n = el ? parseInt(el.textContent.trim(), 10) : NaN;
      return Number.isNaN(n) ? 0 : n;
    };
    let lastCount = readCount();
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
      const count =
        data && data.cart && typeof data.cart.item_count === 'number' ? data.cart.item_count : readCount();
      if (count > lastCount) openDrawer();
      lastCount = count;
    });
  }
}
